import { rrspTaxSavings } from "../../lib/calculators/investments";
import { retirement } from "../../lib/calculators/planning";
import { PROVINCE_LIST, GOV_BENEFITS } from "../../lib/financial-data/canada";
import { CAD } from "../../lib/format";

const provinceOptions = PROVINCE_LIST.map((p) => ({ value: p.code, label: p.name }));

export const rrspTaxSavingsConfig = {
  slug: "rrsp-tax-savings-calculator-canada",
  name: "RRSP Tax Savings Calculator",
  category: "retirement",
  icon: "Receipt",
  tagline: "Estimate your tax refund from an RRSP contribution.",
  applicableYear: 2026,
  lastUpdated: "2026-01-01",
  recommendedProduct: "tfsa-rrsp-planner",
  relatedTools: ["rrsp-growth-calculator-canada", "retirement-calculator-canada"],
  seo: {
    title: "RRSP Tax Savings Calculator Canada (2026) — Estimate Your Refund",
    description: "Estimate the tax refund from your RRSP contribution using 2026 federal and provincial tax brackets. See your marginal rate and effective cost.",
    keywords: ["rrsp tax savings calculator", "rrsp refund calculator canada", "rrsp deduction calculator", "marginal tax rate canada"],
  },
  inputs: [
    { id: "annualIncome", label: "Annual income (before tax)", type: "currency", default: 90000, min: 0 },
    { id: "contribution", label: "RRSP contribution", type: "currency", default: 10000, min: 0 },
    { id: "contributionRoom", label: "Your RRSP room (from CRA, optional)", type: "currency", default: 0, min: 0, help: "Your deduction limit from your CRA Notice of Assessment. Leave 0 to use the 18% estimate." },
    { id: "province", label: "Province / territory", type: "select", default: "ON", options: provinceOptions },
  ],
  compute: (v) => {
    const r = rrspTaxSavings({ annualIncome: v.annualIncome, contribution: v.contribution, province: v.province, room: v.contributionRoom });
    return {
      primary: { label: "Estimated tax refund", value: r.estimatedRefund, type: "currency" },
      secondary: [
        { label: "Marginal tax rate", value: r.marginalRate, type: "percent" },
        { label: "Effective cost of contribution", value: r.effectiveCost, type: "currency", hint: "After refund" },
        { label: "Tax before contribution", value: r.taxBefore, type: "currency" },
        { label: "Tax after contribution", value: r.taxAfter, type: "currency" },
        { label: r.roomFromCRA ? "Your RRSP room" : "Est. RRSP room (18%)", value: r.effectiveRoom, type: "currency", hint: r.withinRoom ? "Within room" : "Exceeded" },
      ],
      chart: {
        type: "bar",
        title: "Your contribution: refund vs net cost",
        data: [
          { x: "Refund", Amount: Math.round(r.estimatedRefund) },
          { x: "Net cost", Amount: Math.round(r.effectiveCost) },
        ],
        xKey: "x",
        series: [{ key: "Amount", name: "Amount" }],
      },
      warning: r.withinRoom ? null : `Your ${CAD(v.contribution)} contribution exceeds your ${r.roomFromCRA ? "" : "estimated "}RRSP room of ${CAD(r.effectiveRoom)} by ${CAD(r.excess)}. Amounts over the $2,000 lifetime buffer are taxed 1% per month — confirm your room in CRA My Account.`,
    };
  },
  content: {
    howItWorks: "RRSP contributions are deducted from your taxable income, so your refund equals your contribution multiplied by your marginal tax rate. This tool calculates your tax before and after the contribution using 2026 federal and provincial brackets, and shows the 'effective cost' — what the contribution really costs you after the refund.",
    inputsExplained: [
      { term: "Annual income", desc: "Your gross employment/self-employment income before tax." },
      { term: "RRSP contribution", desc: "How much you plan to contribute (and deduct)." },
      { term: "Province", desc: "Provincial brackets vary, changing your marginal rate and refund." },
    ],
    example: "An Ontario resident earning $90,000 who contributes $10,000 could get a refund of roughly $3,000, meaning the contribution effectively costs about $7,000.",
    assumptions: ["Uses confirmed 2026 federal and provincial/territorial brackets (CRA).", "Ignores tax credits and other deductions.", "Assumes contribution is fully deductible this year."],
    limitations: ["Simplified — ignores credits, clawbacks and other income.", "Not tax advice; consult a professional.", "Refund depends on your total tax situation."],
  },
  faq: [
    { q: "How is the refund calculated?", a: "Your refund is roughly your contribution times your marginal tax rate — the rate on your top dollar of income. Higher earners get bigger refunds per dollar contributed." },
    { q: "Should I always max my RRSP?", a: "Not always. If you expect a higher tax rate in retirement, a TFSA may be better. It depends on your situation — this tool helps you compare." },
  ],
};

export const retirementConfig = {
  slug: "retirement-calculator-canada",
  name: "Retirement Calculator",
  category: "retirement",
  icon: "Landmark",
  tagline: "See if you're on track for the retirement you want.",
  applicableYear: 2026,
  lastUpdated: "2026-01-01",
  recommendedProduct: "retirement-planner",
  relatedTools: ["rrsp-growth-calculator-canada", "tfsa-growth-calculator-canada"],
  seo: {
    title: "Retirement Calculator Canada (2026) — CPP, OAS & Savings Projection",
    description: "Are you on track to retire? Project your retirement nest egg with RRSP/TFSA savings plus configurable CPP and OAS assumptions.",
    keywords: ["retirement calculator canada", "cpp oas calculator", "how much to retire canada", "retirement savings calculator"],
  },
  inputs: [
    { id: "currentAge", label: "Current age", type: "number", default: 35, min: 18, max: 75 },
    { id: "retirementAge", label: "Retirement age", type: "number", default: 65, min: 50, max: 80 },
    { id: "lifeExpectancy", label: "Plan until age", type: "number", default: 90, min: 70, max: 105 },
    { id: "currentSavings", label: "Current retirement savings", type: "currency", default: 80000, min: 0 },
    { id: "monthlyContribution", label: "Monthly contribution", type: "currency", default: 800, min: 0 },
    { id: "preReturn", label: "Return before retirement", type: "percent", default: 6, min: 0, max: 20, step: 0.1, suffix: "%" },
    { id: "postReturn", label: "Return during retirement", type: "percent", default: 4, min: 0, max: 20, step: 0.1, suffix: "%" },
    { id: "desiredMonthlyIncome", label: "Desired monthly income (today's $)", type: "currency", default: 5000, min: 0 },
    { id: "cppMonthly", label: "Expected CPP (monthly)", type: "currency", default: Math.round(GOV_BENEFITS.cpp.avgMonthly), min: 0, help: `Average CPP at 65 is about ${CAD(GOV_BENEFITS.cpp.avgMonthly)} (verify).` },
    { id: "oasMonthly", label: "Expected OAS (monthly)", type: "currency", default: Math.round(GOV_BENEFITS.oas.maxMonthly65to74), min: 0, help: `Max OAS (65-74) is about ${CAD(GOV_BENEFITS.oas.maxMonthly65to74)} (verify).` },
  ],
  compute: (v) => {
    const r = retirement(v);
    return {
      primary: { label: "Projected nest egg at retirement", value: r.projectedNestEgg, type: "currency" },
      secondary: [
        { label: "Capital you'll need", value: r.capitalRequired, type: "currency" },
        { label: r.onTrack ? "Projected surplus" : "Projected shortfall", value: Math.abs(r.surplusOrGap), type: "currency" },
        { label: "CPP + OAS (monthly)", value: r.govMonthly, type: "currency" },
        { label: "Needed from savings (monthly)", value: r.fromSavingsMonthly, type: "currency" },
        { label: "Extra monthly saving needed", value: r.extraMonthlyNeeded, type: "currency", hint: r.onTrack ? "You're on track!" : "To close the gap" },
      ],
      chart: { type: "line", title: "Nest egg growth to retirement", data: r.series.map((s) => ({ x: `Age ${s.year}`, Savings: s.balance })), xKey: "x", series: [{ key: "Savings", name: "Retirement savings" }] },
      status: r.onTrack ? { type: "success", text: "On track — your projected savings meet your retirement income goal." } : { type: "warning", text: `Projected shortfall. Consider saving about ${CAD(r.extraMonthlyNeeded)} more per month.` },
    };
  },
  content: {
    howItWorks: "This calculator grows your current savings plus monthly contributions to your retirement age, then checks whether that nest egg — combined with CPP and OAS — can fund your desired income for the length of your retirement. CPP and OAS are entered as configurable assumptions because your actual amounts depend on your contribution history and residency.",
    inputsExplained: [
      { term: "Desired monthly income", desc: "The income you want in retirement, in today's dollars." },
      { term: "CPP / OAS", desc: "Government benefits, entered as assumptions. Most people receive less than the maximum CPP." },
      { term: "Return before/after", desc: "Often a higher growth rate while working and a more conservative rate in retirement." },
    ],
    example: "A 35-year-old with $80,000 saved, adding $800/month at 6%, could build roughly $1M by 65 — which, with CPP and OAS, may support around $5,000/month.",
    assumptions: ["Constant returns and contributions.", "CPP/OAS are user assumptions, not verified entitlements.", "Figures are simplified and not inflation-indexed unless you adjust returns."],
    limitations: ["Does not model taxes on withdrawals or OAS clawback.", "Does not guarantee returns or benefit amounts.", "Consult a financial planner for a personalized plan."],
  },
  faq: [
    { q: "How much CPP will I get?", a: `It depends on your contribution history. The average at 65 is about ${CAD(GOV_BENEFITS.cpp.avgMonthly)}/month and the maximum about ${CAD(GOV_BENEFITS.cpp.maxMonthlyAt65)}. Check your CPP Statement of Contributions.` },
    { q: "Is this inflation-adjusted?", a: "Enter returns net of inflation (real returns) for a today's-dollars view, or use nominal returns for future-dollar figures." },
  ],
};
